/**
 * Date input validation helpers.
 *
 * Prevents years with more than 4 digits and provides inline error messages
 * for all date / datetime-local inputs.
 */

/** Maximum value for type="date" inputs (prevents >4-digit years) */
export const DATE_INPUT_MAX = '9999-12-31';

/** Maximum value for type="datetime-local" inputs */
export const DATETIME_LOCAL_INPUT_MAX = '9999-12-31T23:59';

/**
 * Sanitizes a date string by truncating the year portion to 4 digits.
 * Works with both date (YYYY-MM-DD) and datetime-local (YYYY-MM-DDTHH:MM) formats.
 */
export function sanitizeDateValue(value: string): string {
  if (!value) return value;
  const parts = value.split('-');
  if (parts[0] && parts[0].length > 4) {
    parts[0] = parts[0].slice(0, 4);
    return parts.join('-');
  }
  return value;
}

/**
 * Returns an inline error message for an invalid date value, or null if valid.
 */
export function getDateInputError(value: string): string | null {
  if (!value) return null;

  const yearStr = value.split('-')[0];
  if (yearStr && yearStr.length > 4) {
    return 'Year must be 4 digits';
  }

  const date = new Date(value);
  if (isNaN(date.getTime())) {
    return 'Invalid date';
  }

  const year = parseInt(yearStr, 10);
  if (year < 1900) {
    return 'Year must be 1900 or later';
  }

  return null;
}

/**
 * Returns the max attribute value for a given input type.
 */
export function getDateInputMax(type: string): string | undefined {
  if (type === 'date') return DATE_INPUT_MAX;
  if (type === 'datetime-local') return DATETIME_LOCAL_INPUT_MAX;
  return undefined;
}
